import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Modal, Form, Input, message, Tag, Space } from 'antd';
import { PlusOutlined, TeamOutlined } from '@ant-design/icons';
import { ccgService, CcgGroup } from '../../services/ccgService';
import './CCG.scss';

const CCGPage: React.FC = () => {
  const [groups, setGroups] = useState<CcgGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const customerId = 1;

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const data = await ccgService.getGroupsByCustomer(customerId);
      setGroups(data);
    } catch (error) {
      console.error('Failed to fetch CCG groups:', error);
      message.error('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async (values: { name: string }) => {
    try {
      await ccgService.createGroup(customerId, values.name);
      message.success('Group created successfully');
      setIsModalVisible(false);
      form.resetFields();
      fetchGroups();
    } catch (error) {
      console.error('Failed to create group:', error);
      message.error('Failed to create group');
    }
  };

  const columns = [
    {
      title: 'Group ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: 'Group Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Owner ID',
      dataIndex: 'ownerCustomerId',
      key: 'ownerCustomerId',
    },
    {
      title: 'Max Members',
      dataIndex: 'maxMembers',
      key: 'maxMembers',
      width: 120,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const color = status === 'ACTIVE' ? 'green' : 'red';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: CcgGroup) => (
        <Space>
          <Button type="link" size="small">
            View Members
          </Button>
          <Button type="link" size="small">
            Invite
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="ccg-page">
      <div className="page-header">
        <h2>
          <TeamOutlined /> Closed Customer Groups (CCG)
        </h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalVisible(true)}>
          Create Group
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={groups}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'No groups found. Create your first group!' }}
        />
      </Card>

      <Modal
        title="Create New CCG Group"
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateGroup}>
          <Form.Item
            label="Group Name"
            name="name"
            rules={[
              { required: true, message: 'Please enter group name' },
              { max: 100, message: 'Group name must be less than 100 characters' },
            ]}
          >
            <Input placeholder="e.g., Family Group, Friends Circle" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Create Group
              </Button>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CCGPage;
